import { useLocation } from "react-router-dom";

/**
 * The navigation model: seven sections, the pages inside them, and — new in the
 * P90 redesign — one level of NESTING (Books → Reports → {Trial balance, Balance
 * sheet, Profit & loss}).
 *
 * ⚠️ THE NAV IS A FLY-OUT MODEL NOW, NOT AN INLINE EXPAND. The old sidebar
 * unfolded a section's pages as rows BENEATH the lit section, which pushed the
 * column down and gave the sidebar a measured height ceiling (the old
 * `e2e/specs/nav.spec.ts`). The redesign (Sarah/Jonathan, 2026-10-20) opens
 * each section's pages in a small fly-out panel BESIDE the row instead: nothing
 * expands inline, so nothing pushes down, so the height ceiling is gone for
 * good and a section can hold as many pages as it needs. `Shell.tsx` draws the
 * panels; this file is only the model and the path→section/tab matching.
 *
 * A `tabs: []` section is a plain link with no fly-out (Dashboard, Accounts).
 * A tab with `children` opens a SECOND fly-out beside the first — the one level
 * of nesting the direction asks for, and so far only Reports uses it.
 *
 * Nothing here changes a route. Every path is the path it always was, so every
 * deep link, bookmark and cross-page `<Link>` still lands. The only structural
 * move the redesign makes to the pages themselves (this phase) is folding
 * Entities INTO the Accounts explorer — so "Entities" leaves the nav while
 * `/entities` and `/entities/:id` keep resolving for deep links and drill-downs.
 */

export interface TabSpec {
  to: string;
  label: string;
  /** Matched with startsWith, so /accounts/:id keeps the Accounts tab lit, and
   *  a parent (Reports) lights on any of its children's paths. */
  match?: string[];
  end?: boolean;
  /** The quiet right-aligned gloss in the fly-out ("cash today", "in / out"). */
  hint?: string;
  /** One level of nesting: this tab opens a sub-fly-out of these. */
  children?: TabSpec[];
}

export interface SectionSpec {
  key: string;
  label: string;
  to: string;
  icon: keyof typeof ICONS;
  tabs: TabSpec[];
  /**
   * ⚠️ A section that HAS pages but renders as a PLAIN LINK in the sidebar, with
   * NO fly-out (Settings, 2026-10-22). Its `tabs` still power the breadcrumb, the
   * row-lighting and the phone Settings menu — but on desktop the pages live in a
   * persistent in-page side rail (`SettingsLayout`) beside the main nav, the way
   * Clio/Render do Settings, instead of a hover panel. Clicking the row navigates
   * to `to`. Default (undefined) = fly-out, the normal P90 behaviour.
   */
  flyout?: boolean;
}

export const SECTIONS: SectionSpec[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    to: "/",
    icon: "home",
    tabs: [],
  },
  {
    // ⚠️ THE EXPLORER + its working companions (redesign/dashboard, 2026-10-22).
    // Accounts and Entities merged in P90 (the Accounts page pivots by Entity /
    // Owner / Category, with EntityDetail the drill-down). The dashboard redesign
    // adds a fly-out so the two things you do AGAINST accounts sit beside them:
    //   • Commitments — moved here from Cash (they change available cash, and you
    //     manage them per account/entity).
    //   • Balances — manual balance entry (was /readings in Settings). It lives
    //     here "because it needs constant updating" (Sarah, 2026-10-22); Needs You
    //     only FLAGS a stale balance, the entry itself happens here.
    // `/entities` and `/entities/:id` still resolve and light this row.
    key: "accounts",
    label: "Balances",
    to: "/accounts",
    icon: "wallet",
    tabs: [
      { to: "/accounts", label: "Accounts", match: ["/accounts", "/entities"] },
      { to: "/commitments", label: "Commitments" },
      { to: "/readings", label: "Manual Balances" },
    ],
  },
  {
    // Banks is the bank side; Books is the book side. They sit next to each
    // other because that adjacency IS the architecture (018_conductor.sql keeps
    // the Plaid feed and the QuickBooks book of record apart). Consolidate
    // within each; never fold one into the other.
    key: "banks",
    label: "Banks",
    icon: "bank",
    to: "/transactions",
    // One page now (P90): Bank transactions carries the Counterparties cut as an
    // in-page view, and Connections (with Institutions + Held folded in) moved to
    // Settings → Setup (Jonathan, 2026-10-20). A plain link, no flyout.
    tabs: [],
  },
  {
    key: "books",
    // "Finance" (Sarah, 2026-10-22). Was "Books" → "QuickBooks"; renamed again to
    // an all-encompassing name as more of the money/book-of-record work gathers
    // here. Route and key stay /books. NOTE: not a merge — Banks (the Plaid feed)
    // stays its own section; the Plaid-vs-QuickBooks separation is deliberate.
    label: "Finance",
    to: "/books",
    icon: "book",
    tabs: [
      { to: "/books", label: "Chart of accounts", end: true },
      { to: "/books/transactions", label: "Book transactions" },
      // The bank-classifier's rules workbench: sign off the accounting rules and
      // approve the vendor candidates it learned (Motivated Care pilot). Lives
      // on the book side because it is about which QuickBooks account a line
      // becomes. Read-only to QuickBooks.
      { to: "/books/rules", label: "Classification rules" },
      // Upload the Fingercheck payroll register for a pay run (#17, Sarah
      // 2026-10-25). Lives under Finance because it feeds the Class E split.
      { to: "/payroll", label: "Payroll" },
      // ⚠️ THE ONE NESTED FLY-OUT (Sarah/Jonathan, 2026-10-20). Reports opens a
      // second panel beside the first with the three statements. It also lands
      // on the trial balance when clicked directly, and `match` lights it on
      // any of the three.
      {
        to: "/books/trial-balance",
        label: "Reports",
        match: [
          "/books/trial-balance",
          "/books/balance-sheet",
          "/books/profit-loss",
        ],
        children: [
          { to: "/books/trial-balance", label: "Trial balance" },
          { to: "/books/balance-sheet", label: "Balance sheet" },
          { to: "/books/profit-loss", label: "Profit & loss" },
        ],
      },
      { to: "/books/ask", label: "Ask" },
      { to: "/books/sync", label: "Sync" },
    ],
  },
  {
    key: "payables",
    label: "Payables",
    to: "/invoices",
    icon: "invoice",
    tabs: [
      // Where bills now enter: the ap@ mailbox, read and queued for review (081).
      { to: "/ap-inbox", label: "AP Inbox" },
      { to: "/invoices", label: "Invoices" },
      { to: "/approvals", label: "Approvals" },
      { to: "/payments", label: "Payments" },
      // "Vendor Center", not "Vendors" (P91); the route is still /vendors.
      { to: "/vendors", label: "Vendor Center" },
    ],
  },
  {
    // Operations (redesign/dashboard, 2026-10-22; renamed from "Needs You").
    // The action surface: everything that needs WORK — exceptions (low balances,
    // re-auths, stale, held, approvals, floors), the bookkeeping bands, and the
    // system-health footer — all OFF the Dashboard, which is now a read-only
    // statement. Client-visible but scoped to the viewer's reach. Plain link.
    // `/needs-you` redirects here; the old Settings "Alerts" (who-gets-emailed)
    // is a separate thing and went back to Settings as "Alert emails".
    key: "operations",
    label: "Operations",
    to: "/operations",
    icon: "list",
    tabs: [],
  },
  {
    // Settings: the "no black box" home, at the bottom under the work.
    //
    // ⚠️ NO FLY-OUT (Jonathan, 2026-10-22). The three-GROUPS-with-sub-pages
    // model was confusing ("hard to find what you're looking for"), so Settings
    // now works the way Clio/Render do it: the sidebar row is a plain link, and
    // every settings page is listed FLAT in a persistent side rail that sits
    // in-page beside the main nav (`SettingsLayout` + `.settings-rail`). These
    // `tabs` are that flat list — one real page each, no groups — and they still
    // drive the breadcrumb, the sidebar row-lighting (`pathsOf`) and the phone
    // Settings menu. The in-page rail reads the same set (plus the two
    // Connections `?v=` cuts, which it resolves itself). Routes are unchanged.
    key: "settings",
    label: "Settings",
    to: "/floors",
    icon: "gear",
    flyout: false,
    tabs: [
      // /institutions and /connections/held redirect into /connections?v=…, so
      // the Connections row owns them; /readings (enter balances by hand) is
      // reached from Connections, so it lights that row too.
      { to: "/floors", label: "Cash floors" },
      // The two email-recipient screens (Alert emails, Snapshot emails) moved
      // into the System page, stacked (Jonathan, 2026-11-03), to keep this list
      // short; /alerts and /snapshot-emails redirect to System.
      {
        to: "/connections",
        label: "Connections",
        match: ["/connections", "/institutions"],
      },
      // People + Activity merged into one entry (Jonathan, 2026-11-03): two
      // pages behind an in-page switch, /activity redirecting under /people.
      { to: "/people", label: "People / Activity", match: ["/people", "/activity"] },
      { to: "/feedback", label: "Feedback" },
      { to: "/settings/system", label: "System" },
      // The history of the reload prompt's "what's new" notes, kept so a change
      // announced once can be found again (Jonathan, 2026-11-03).
      { to: "/settings/whats-new", label: "What's new" },
    ],
  },
];

/** Every path a tab owns, following children, for lighting the row and matching. */
function pathsOfTab(tab: TabSpec): string[] {
  const own = tab.match ?? [tab.to];
  const kids = tab.children?.flatMap(pathsOfTab) ?? [];
  return [...own, ...kids];
}

/** Every path a section owns, for lighting up the sidebar row. */
function pathsOf(section: SectionSpec): string[] {
  if (section.tabs.length === 0) return [section.to];
  return section.tabs.flatMap(pathsOfTab);
}

export function sectionForPath(pathname: string): SectionSpec | null {
  // Longest prefix wins, so /connections/held picks Banks rather than falling
  // through to whichever section happened to be declared first.
  let best: { section: SectionSpec; length: number } | null = null;
  for (const section of SECTIONS) {
    if (section.to === "/" && pathname === "/") return section;
    for (const path of pathsOf(section)) {
      if (path === "/") continue;
      if (pathname === path || pathname.startsWith(`${path}/`)) {
        if (!best || path.length > best.length) {
          best = { section, length: path.length };
        }
      }
    }
  }
  return best?.section ?? null;
}

export function tabIsActive(tab: TabSpec, pathname: string): boolean {
  if (tab.end && !tab.children) return pathname === tab.to;
  const paths = pathsOfTab(tab);
  return paths.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

/**
 * The active trail inside a section: the top-level tab that is lit, and — when
 * that tab has children — the lit child. Breadcrumb and PageTitle both read it,
 * so the sidebar, the crumb and the H1 can never disagree about where you are.
 */
export function activeTrail(
  section: SectionSpec,
  pathname: string,
): { tab: TabSpec | null; child: TabSpec | null } {
  const tab = section.tabs.find((t) => tabIsActive(t, pathname)) ?? null;
  const child =
    tab?.children?.find((c) => tabIsActive(c, pathname)) ?? null;
  return { tab, child };
}

/**
 * The H1 of a page.
 *
 * On a page inside a section it is the deepest active label — the child
 * ("Balance sheet") if on one, else the tab ("Position"), else the section.
 * On a page outside any section, or a detail page, `title` is what shows.
 */
export function PageTitle({ title, className }: { title?: string; className?: string }) {
  const { pathname } = useLocation();
  const section = sectionForPath(pathname);
  const { tab, child } = section
    ? activeTrail(section, pathname)
    : { tab: null, child: null };
  return (
    <h1 className={className}>
      {child?.label ?? tab?.label ?? title ?? section?.label ?? ""}
    </h1>
  );
}

/**
 * Sixteen-pixel line icons, inline so there is no icon package and no network
 * request. `currentColor` throughout, which is what lets the sidebar's active
 * state recolour them without a second rule.
 */
const ICONS = {
  home: "M3 7.4 8.5 3l5.5 4.4V14H9.9v-4H7.1v4H3z",
  chart: "M2.5 13.5h11M4.5 11V6.5M7.8 11V3.5M11.1 11V8",
  wallet: "M2.5 5.2A1.7 1.7 0 0 1 4.2 3.5h7.6A1.7 1.7 0 0 1 13.5 5.2v6.6a1.7 1.7 0 0 1-1.7 1.7H4.2a1.7 1.7 0 0 1-1.7-1.7zM10 8.5h2.2",
  swap: "M3 5.5h9M9.6 3.1 12 5.5 9.6 7.9M13 10.5H4M6.4 8.1 4 10.5l2.4 2.4",
  bank: "M2.5 6.5 8 3l5.5 3.5M3.8 6.9v5.2M6.6 6.9v5.2M9.4 6.9v5.2M12.2 6.9v5.2M2.5 13.2h11",
  list: "M5.5 4.5h8M5.5 8h8M5.5 11.5h8M2.6 4.5h.01M2.6 8h.01M2.6 11.5h.01",
  // An open ledger: two leaves and the spine between them. Deliberately not a
  // second wallet or a second bank -- Books has to be told apart from Accounts
  // and from Banks at sidebar size, and those two already own the rounded
  // rectangle and the columned building.
  book: "M8 5c-1.4-1-3-1.4-5.2-1.4v8.2c2.2 0 3.8.4 5.2 1.4M8 5c1.4-1 3-1.4 5.2-1.4v8.2c-2.2 0-3.8.4-5.2 1.4M8 5v8.2",
  // A document with lines on it. Not a dollar sign and not a card: Cash and
  // Accounts already own money-shaped marks, and what distinguishes payables
  // at 16px is that it is a stack of PAPER somebody has to work through.
  invoice: "M4 2.5h6.2l2.3 2.3v8.7H4zM10 2.6v2.6h2.6M6.2 8h4.1M6.2 10.4h4.1",
  // ⚠️ SLIDERS, not a cog (Jonathan + Sarah, 2026-10-25). The old gear was a hub
  // with eight radial spokes — which at 16px is indistinguishable from the theme
  // toggle's sun, and on the phone top bar the two sit side by side. An
  // adjustments/sliders mark (three tracks, each with a knob) reads as "settings"
  // and shares no shape with the sun or the moon. The knobs are drawn filled in
  // the Icon component below; this path is the three tracks.
  gear: "M2.5 4.5H13.5M2.5 8H13.5M2.5 11.5H13.5",
} as const;

export function Icon({ name }: { name: keyof typeof ICONS }) {
  const filled = name === "home";
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      aria-hidden="true"
      fill={filled ? "currentColor" : "none"}
      stroke={filled ? "none" : "currentColor"}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={ICONS[name]} />
      {/* The sliders' knobs — filled dots sitting on the tracks, offset so the
          mark reads as "adjust", not three plain lines (which would echo the
          Operations list icon). */}
      {name === "gear" ? (
        <g fill="currentColor" stroke="none">
          <circle cx="10.5" cy="4.5" r="1.9" />
          <circle cx="5.5" cy="8" r="1.9" />
          <circle cx="10.5" cy="11.5" r="1.9" />
        </g>
      ) : null}
    </svg>
  );
}

/** A small chevron that points right (fly-out) — used on rows that open one. */
export function FlyoutCaret() {
  return (
    <svg
      className="nav-caret"
      width="12"
      height="12"
      viewBox="0 0 16 16"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 4l4 4-4 4" />
    </svg>
  );
}
